import { useContext, useState, useEffect } from "react"
import { Field, useFormikContext } from "formik"
import { UserContext } from "../../context/userContext"
import { FormContext } from "../../context/formContext"
import { InputText as Input } from "primereact/inputtext"
import { useLazyQuery } from "@apollo/client"
import { IS_ORGANIZATION_NAME_UNIQUE } from "./queries/queries"
import { trimTextValue } from "./helpers/trimTextValue"
import styles from "../../styles/Form.module.css"

export default function TypeOrganizationName(props) {
  const { field, isRequired, index, initialValue } = props
  const formik = useFormikContext()
  const userCtx = useContext(UserContext)
  const form = useContext(FormContext)
  const [validMessage, setValidMessage] = useState(null)
  const [isChecking, setIsChecking] = useState(false)

  // Get enterprise_id and organization_id from context or formik
  const { enterprise_id } = userCtx.session.user
  const organization_id = form.organization_id || formik.values.organization_id // Here we get the organization_id from the context

  useEffect(() => {
    // Register field as loading in FormContext
    form && form.registerLoadingField(field)

    // Notify FormContext that field is loaded
    form && form.completeLoadingField(field)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (form.editMode && formik.values.organization_name === initialValue) {
      // If we are in edit mode and the name hasn't changed, don't perform validation
      setValidMessage(null)
    }
  }, [form.editMode, formik.values.organization_name, initialValue])

  // useLazyQuery to execute the query manually
  const [checkNameUnique, { error }] = useLazyQuery(
    IS_ORGANIZATION_NAME_UNIQUE,
    {
      fetchPolicy: "network-only",
      onCompleted: () => setIsChecking(false),
    }
  )

  const handleBlur = async (e) => {
    formik.handleBlur(e)

    const currentName = formik.values.organization_name?.trim()
    if (!currentName || currentName === initialValue) {
      return // Don't perform validation if the name hasn't changed or is empty
    }

    setIsChecking(true)

    try {
      const response = await checkNameUnique({
        variables: {
          enterprise_id: enterprise_id, // We pass the enterprise_id
          organization_name: currentName, // Validate the organization name
          organization_id: form.editMode ? organization_id : null, // Use organization_id if in edit mode
        },
      })

      const isUnique = response?.data?.isUniqueOrganization

      if (!isUnique) {
        formik.setFieldError(
          "organization_name",
          "The organization name must be unique within the enterprise."
        )
        setValidMessage(null)
      } else {
        // Clear the uniqueness error if valid
        if (formik.errors.organization_name) {
          formik.setFieldError("organization_name", undefined)
        }
        setValidMessage("The organization name is valid.")
      }
    } catch (err) {
      console.error("Error verifying organization name uniqueness:", err)
      formik.setFieldError(
        "organization_name",
        "Error checking the organization name."
      )
      setValidMessage(null)
    } finally {
      setIsChecking(false)
    }
  }

  return (
    <div
      className={styles.field}
      style={{
        order: index,
        display: "block", // Display handling is done in FieldBuilder
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>
      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}
      <div className={styles.value}>
        <Field name={field.field_name}>
          {({ field: formikField, meta }) => (
            <>
              <Input
                name={formikField.name}
                id={formikField.name}
                value={formikField.value}
                onChange={(e) => formikField.onChange(trimTextValue(e))}
                disabled={isChecking}
                onBlur={handleBlur}
                autoComplete="off"
              />
              {meta.touched && meta.error && (
                <div className={styles.errFieldMsg}>{meta.error}</div>
              )}
              {isChecking && (
                <div className={styles.infoFieldMsg}>Checking uniqueness...</div>
              )}
              {error && (
                <div className={styles.errFieldMsg}>
                  Error checking the organization name.
                </div>
              )}
              {!isChecking && !meta.error && validMessage && (
                <div className={styles.validFieldMsg}>{validMessage}</div>
              )}
            </>
          )}
        </Field>
      </div>
    </div>
  )
}
